import React, { memo, useEffect, useState } from "react";
import Modal from "./common/Modal";
import {
  BASE_URL,
  deleteHelper,
  getToken,
  postHelper,
  removeAccents,
} from "../general/helper";
import { AiOutlineMinus, AiOutlinePlus } from "react-icons/ai";
import Delete from "./common/Delete";
import Alert from "./common/Alert";
import { BsFillPeopleFill } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActivityPricesCard from "./ActivityPricesCard";

const Activity = memo(
  ({
    formattedSelectedDay,
    which,
    setBookIsDone,
    selectedActivity,
    isActivityOpen,
    closeActivityModal,
    user,
    general,
    lang,
  }) => {
    const [numberOfAdults, setNumberOfAdults] = useState(1); // State for numberOfAdults
    const [adultsNames, setAdultsNames] = useState([]); // State for adultsNames
    const [notes, setNotes] = useState("");
    const [availableSeats, setAvailableSeats] = useState(undefined);
    const [alert, setAlert] = useState({
      alert: "hidden",
      error: false,
      message: "",
      timeout: undefined,
    });
    const navigate = useNavigate();
    const location = useLocation();

    const { t } = useTranslation();

    useEffect(() => {
      const maxAttendants = selectedActivity?.capacity?.maxAttendants;
      if (maxAttendants) {  
        const occurrenceYmd =
          selectedActivity?.start?.slice(0, 10) || formattedSelectedDay;
        const currentEvent = selectedActivity.info?.find(
          (i) => i?.date === occurrenceYmd,
        );
        if (currentEvent) {
          setAvailableSeats(
            Number(maxAttendants - currentEvent.totalAttendants),
          );
        } else {
          setAvailableSeats(Number(maxAttendants));
        }
      }
    }, [formattedSelectedDay, selectedActivity]);

    useEffect(() => {
      if (user) {
        const initialNames = user.firstName
          ? [`${user.firstName} ${user.lastName}`]
          : [];
        setAdultsNames(initialNames);
      }
    }, [user]);

    const handleNameChange = (event, index) => {
      const updatedNames = [...adultsNames];
      updatedNames[index] = event.target.value; // Update the adultsNames at the given index
      setAdultsNames(updatedNames); // Update the adultsNames state
    };

    const handleAddActivity = async () => {
      // Check if availableSeats is undefined or there are enough seats
      const seatsAvailable =
        typeof availableSeats === "undefined" ||
        availableSeats - numberOfAdults >= 0;

      // Check if the number of adults matches the length of adultsNames
      const correctNumberOfAdults = numberOfAdults === adultsNames.length;

      // Check if all names in adultsNames are not empty
      const allNamesValid = adultsNames.every((name) => name !== "");
      if (seatsAvailable && correctNumberOfAdults && allNamesValid) {
        if (!user) {
          const existingSearchParams = new URLSearchParams(location.search);
          // console.log(existingSearchParams);
          // Navigate to the 2FA authentication page
          navigate(`/2fa?${existingSearchParams}`, {
            state: {
              activityId: selectedActivity.id,
              adultsNames,
              notes,
              numberOfAdults,
            },
          });
          return;
        }
        const bookActivity = await postHelper(
          `${BASE_URL}/activitiesSchedule/guest/events/${selectedActivity.id}/bookings`,
          JSON.stringify({
            adultsNames,
            notes,
            numberOfAdults,
          }),
          { authorization: `token ${getToken()}` },
        );
        if (!bookActivity?.participantId) {
          setAlert({
            alert: "visible",
            message:
              bookActivity?.response?.data?.message ||
              "We're sorry, but your booking couldn't be completed at this moment",
            error: true,
            timeout: 4000,
          });
        } else {
          setBookIsDone();
          closeActivityModal();
        }
      } else {
        if (numberOfAdults !== adultsNames.length) {
          setAlert({
            alert: "visible",
            message: "Please complete details for all participants",
            error: true,
            timeout: undefined,
          });
        } else {
          setAlert({
            alert: "visible",
            message:
              "Apologies, but you have exceeded the number of available places",
            error: true,
            timeout: undefined,
          });
        }
      }
    };

    const handleDeleteEvent = async () => {
      const deleteEvent = await deleteHelper(
        `${BASE_URL}/activitiesSchedule/guest/events/${selectedActivity.id}/bookings`,
        { authorization: `token ${getToken()}` },
      );
      if (deleteEvent !== "OK") {
        setAlert({
          alert: "visible",
          error: true,
          message:
            "We're sorry, but we were unable to cancel your reservation at this time.",
          timeout: undefined,
        });
      } else {
        setBookIsDone();
        closeActivityModal();
      }
    };

    return (
      <Modal isOpen={isActivityOpen} onClose={closeActivityModal}>
        <div className="flex flex-col">
          <div className="mx-4">
            <div className="flex">
              <p>
                {selectedActivity?.activityName ||
                  selectedActivity?.activity?.name?.text}
              </p>
              <p className="ml-4 font-semibold">
                {selectedActivity?.start?.slice(11, 16)}
              </p>
            </div>
            <p className="text-blue-500 text-sm italic">
              @
              {selectedActivity?.activityName ||
                selectedActivity?.activity?.tags[0]}
            </p>

            <p className="text-gray-700 text-sm mt-1">
              {selectedActivity.activity?.locationName?.text}
            </p>
          </div>
          {selectedActivity?.activity?.sliceImagesUrl && (
            <img
              src={`${BASE_URL}/${selectedActivity?.activity?.sliceImagesUrl[0]}`}
              alt="Activity"
              className="mt-4 w-full h-24 object-cover rounded-lg"
            />
          )}
          {(selectedActivity?.tagDescription ||
            selectedActivity?.activity?.description?.[lang] || selectedActivity?.activity?.description?.en || selectedActivity?.activity?.description?.text) && (
            <p className="m-4">
              {selectedActivity?.tagDescription ||
                selectedActivity?.activity?.description?.[lang] || selectedActivity?.activity?.description?.en || selectedActivity?.activity?.description?.text}
            </p>
          )}
          {selectedActivity?.activity?.details?.[lang] || selectedActivity?.activity?.details?.en || selectedActivity?.activity?.details?.text && (
            <p className="m-4 text-gray-600 text-sm leading-relaxed">
              {selectedActivity?.activity?.details?.[lang] || selectedActivity?.activity?.details?.en || selectedActivity?.activity?.details?.text}
            </p>
          )}
          {availableSeats && (
            <div className="flex mx-4">
              <p>Available Seats</p>
              <div className="flex items-center">
                <span className="ml-2 font-semibold items-center mr-2  ">
                  {availableSeats}{" "}
                </span>
                <BsFillPeopleFill />
              </div>
            </div>
          )}
          {!user?.isTestUser && (
            <>
              {which === "book" &&
                selectedActivity?.reservationEnabled &&
                availableSeats !== 0 && (
                  <>
                    <div className="grid mx-4">
                      <label htmlFor="notes" className="text-sm font-semibold">
                        Book Activity
                      </label>
                      <textarea
                        id="notes"
                        placeholder="Please type any requests you have for the booking"
                        onChange={(event) => setNotes(event.target.value)}
                        value={notes}
                        className="mt-2 border-2 border-black rounded px-2 py-2 text-base h-16 resize-y"
                      />
                    </div>
                    <div className="flex flex-col mx-4">
                      {Array.from({ length: numberOfAdults }).map(
                        (_, index) => (
                          <input
                            key={index}
                            type="text"
                            placeholder={`Name ${index + 1}`}
                            value={adultsNames[index] || ""}
                            onChange={(event) => handleNameChange(event, index)}
                            className="mt-2 border-2 border-black rounded px-2 py-1 text-base"
                          />
                        ),
                      )}
                    </div>
                    <ActivityPricesCard
                      general={general}
                      activity={selectedActivity?.activity ?? selectedActivity}
                    />
                    <div className="flex items-center justify-between my-2 mx-2 gap-3">
                      {/* Counter */}
                      <div className="flex">
                        <button
                          type="button"
                          onClick={() =>
                            setNumberOfAdults((prev) => Math.max(prev - 1, 1))
                          }
                          className="border-2 border-black rounded-l px-4 py-2 font-semibold text-sm flex items-center justify-center"
                        >
                          <AiOutlineMinus />
                        </button>

                        <div className="bg-gray-300 border-t-2 border-b-2 border-black px-4 py-2 font-semibold text-sm flex items-center justify-center min-w-[48px]">
                          {numberOfAdults}
                        </div>

                        <button
                          type="button"
                          onClick={() => setNumberOfAdults((prev) => prev + 1)}
                          className="border-2 border-black rounded-r px-4 py-2 font-semibold text-sm flex items-center justify-center"
                        >
                          <AiOutlinePlus size={18} />
                        </button>
                      </div>

                      {/* Add button */}
                      <button
                        type="button"
                        onClick={handleAddActivity}
                        className="border-2 border-black px-5 py-2 font-semibold text-base flex items-center justify-center whitespace-nowrap"
                      >
                        {removeAccents(t("addToMyEvents"))}
                      </button>
                    </div>
                  </>
                )}
              {which === "delete" && (
                <>
                  <Delete handleDeleteEvent={handleDeleteEvent} />
                </>
              )}
            </>
          )}
        </div>
        {alert.alert === "visible" && (
          <Alert
            message={alert.message}
            isOpen={alert.error}
            onClose={() =>
              setAlert({
                alert: "hidden",
                message: "",
                error: false,
                timeout: undefined,
              })
            }
            timeout={alert.timeout}
          />
        )}
      </Modal>
    );
  },
);

export default Activity;
